import React, { useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  alpha,
  useMediaQuery,
} from '@mui/material'
import { keyframes } from '@mui/material/styles'
import { colors, spacing, typography } from '../design-system/tokens'
import { AZURE_AD_SCOPE_STRING } from '../config/auth'

// ── Animations ───────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`

const shimmer = keyframes`
  0%   { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`

// ── Microsoft Logo ───────────────────────────────────────────
const MicrosoftLogo = () => (
  <svg width="21" height="21" viewBox="0 0 21 21" fill="none" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
)

// ── Lock Icon ────────────────────────────────────────────────
const LockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="7" width="10" height="7" rx="1.5" />
    <path d="M5 7V5a3 3 0 016 0v2" />
  </svg>
)

// ── Arrow Icon ───────────────────────────────────────────────
const ArrowLeftIcon = () => (
  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M10 3L5 8l5 5" />
  </svg>
)

// ── OAuth helpers ────────────────────────────────────────────
function generateRandomString(length = 32): string {
  const array = new Uint8Array(length)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

const POST_LOGIN_REDIRECT_KEY = 'ignis_post_login_redirect'

function sanitizeRedirectPath(value: string | null): string | null {
  if (!value) return null
  if (!value.startsWith('/')) return null
  if (value.startsWith('//')) return null
  if (value.startsWith('/login')) return null
  if (value.startsWith('/auth/callback/azure-ad')) return null
  return value
}

const Login: React.FC = () => {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const redirectTarget = params.get('redirect')
  const isInvitationLogin = !!redirectTarget?.startsWith('/invitations/')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(() => {
    const storedError = sessionStorage.getItem('ignis_login_error')
    if (storedError) {
      sessionStorage.removeItem('ignis_login_error')
      return storedError
    }
    return null
  })
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')

  const handleAzureADLogin = async () => {
    if (isLoading) return
    setIsLoading(true)
    setError(null)
    const requestedRedirect = sanitizeRedirectPath(new URLSearchParams(location.search).get('redirect'))
    if (requestedRedirect) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, requestedRedirect)
    } else {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY)
    }

    const runtimeConfig = (window as any).runtimeConfig || {}

    const clientId = runtimeConfig.AZURE_AD_CLIENT_ID ||
      import.meta.env.VITE_AZURE_AD_CLIENT_ID ||
      'e39e6a7c-055e-4811-8c43-fa29c99e57c8'
    const tenantId = runtimeConfig.AZURE_AD_TENANT_ID ||
      import.meta.env.VITE_AZURE_AD_TENANT_ID ||
      '487753ef-8cf6-4960-ad5f-9ceb7b82fcd7'
    const redirectUri = runtimeConfig.AZURE_AD_REDIRECT_URI ||
      import.meta.env.VITE_AZURE_AD_REDIRECT_URI ||
      window.location.origin
    const scopes = runtimeConfig.AZURE_AD_SCOPES ||
      import.meta.env.VITE_AZURE_AD_SCOPES ||
      AZURE_AD_SCOPE_STRING
    const normalizedScopes = scopes
      .split(/[\s,]+/)
      .map((scope) => scope.trim())
      .filter(Boolean)
      .join(' ')

    const encodedRedirectUri = encodeURIComponent(redirectUri)
    const encodedScope = encodeURIComponent(normalizedScopes)

    // Generate cryptographically random state and nonce to prevent CSRF and token replay
    const state = generateRandomString()
    const nonce = generateRandomString()
    sessionStorage.setItem('oauth_state', state)
    sessionStorage.setItem('oauth_nonce', nonce)

    const authUrl = `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}&` +
      `response_type=id_token&` +
      `redirect_uri=${encodedRedirectUri}&` +
      `scope=${encodedScope}&` +
      `response_mode=fragment&` +
      `state=${encodeURIComponent(state)}&` +
      `nonce=${encodeURIComponent(nonce)}`

    window.location.href = authUrl
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: colors.background.page,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Warm ambient glow — coral tinted */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: '25%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '700px',
          height: '700px',
          background: `radial-gradient(ellipse, ${alpha(colors.accent.main, 0.05)} 0%, ${alpha(colors.accent.main, 0.02)} 35%, transparent 65%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Subtle top-right accent wash */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          top: '-10%',
          right: '-5%',
          width: '500px',
          height: '500px',
          background: `radial-gradient(ellipse, ${alpha(colors.accent.light, 0.3)} 0%, transparent 70%)`,
          pointerEvents: 'none',
        }}
      />

      {/* Nav bar */}
      <Box
        component="header"
        sx={{
          height: '64px',
          display: 'flex',
          alignItems: 'center',
          px: { xs: 3, md: 7 },
          borderBottom: `1px solid ${colors.border.subtle}`,
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          component="a"
          href="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            borderRadius: spacing.borderRadius.sm,
            '&:focus-visible': {
              outline: `2px solid ${colors.accent.main}`,
              outlineOffset: '2px',
            },
          }}
        >
          <img
            src="/archer_logo.svg"
            alt="Archer"
            style={{ height: '28px', objectFit: 'contain', opacity: 0.9 }}
            onError={(e) => { (e.target as HTMLImageElement).style.display = 'none' }}
          />
        </Box>
      </Box>

      {/* Main content — centered */}
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '420px',
            textAlign: 'center',
          }}
        >
          {/* Headline — uses serif display font */}
          <Typography
            sx={{
              fontFamily: typography.fontFamily.display,
              fontSize: { xs: '2rem', md: '2.35rem' },
              fontWeight: typography.fontWeight.bold,
              letterSpacing: '-0.025em',
              lineHeight: 1.1,
              color: colors.text.heading,
              mb: 1.5,
              animation: prefersReducedMotion ? 'none' : `${fadeUp} 0.7s ease-out both`,
            }}
          >
            Welcome back
          </Typography>
          <Typography
            sx={{
              fontFamily: typography.fontFamily.primary,
              fontSize: typography.fontSize.lg,
              color: colors.text.secondary,
              fontWeight: typography.fontWeight.regular,
              lineHeight: 1.5,
              mb: isInvitationLogin ? 2 : 5,
              animation: prefersReducedMotion ? 'none' : `${fadeUp} 0.7s ease-out 0.08s both`,
            }}
          >
            {isInvitationLogin
              ? 'Sign in to continue with your invitation'
              : 'Sign in to continue to your workspace'}
          </Typography>

          {isInvitationLogin && (
            <Typography
              sx={{
                fontFamily: typography.fontFamily.primary,
                fontSize: typography.fontSize.sm,
                color: colors.text.tertiary,
                mb: 3,
                animation: prefersReducedMotion ? 'none' : `${fadeUp} 0.7s ease-out 0.1s both`,
              }}
            >
              You’ll return to the invitation after Microsoft sign-in completes.
            </Typography>
          )}

          {/* Error alert */}
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                mb: 3,
                borderRadius: spacing.borderRadius.lg,
                border: `1px solid ${alpha(colors.error.main, 0.15)}`,
                fontSize: typography.fontSize.sm,
                '& .MuiAlert-icon': { color: colors.error.main },
              }}
            >
              {error}
            </Alert>
          )}

          {/* Sign-in card */}
          <Box
            sx={{
              background: colors.background.card,
              borderRadius: spacing.borderRadius.xxl,
              border: `1px solid ${colors.border.subtle}`,
              boxShadow: `${spacing.shadow.xs}, ${spacing.shadow.xl}`,
              p: { xs: 3.5, md: 4.5 },
              animation: prefersReducedMotion ? 'none' : `${fadeUp} 0.7s ease-out 0.15s both`,
            }}
          >
            {/* Microsoft SSO button */}
            <Box
              component="button"
              onClick={handleAzureADLogin}
              disabled={isLoading}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '12px',
                py: '15px',
                px: '24px',
                fontFamily: typography.fontFamily.primary,
                fontSize: typography.fontSize.lg,
                fontWeight: typography.fontWeight.medium,
                color: colors.text.primary,
                background: colors.background.page,
                border: `1px solid ${colors.border.primary}`,
                borderRadius: spacing.borderRadius.card,
                cursor: isLoading ? 'wait' : 'pointer',
                transition: `border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, transform 0.2s ease, opacity 0.2s ease`,
                letterSpacing: '-0.005em',
                opacity: isLoading ? 0.7 : 1,
                position: 'relative',
                overflow: 'hidden',
                '&:hover:not(:disabled)': {
                  borderColor: colors.border.hover,
                  boxShadow: `0 2px 8px ${alpha(colors.accent.main, 0.08)}`,
                  background: colors.background.cardHover,
                },
                '&:active:not(:disabled)': {
                  transform: 'scale(0.995)',
                },
                '&:focus-visible': {
                  outline: `2px solid ${colors.accent.main}`,
                  outlineOffset: '2px',
                },
                // Shimmer on loading
                ...(isLoading && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: `linear-gradient(90deg, transparent 0%, ${alpha(colors.accent.main, 0.04)} 50%, transparent 100%)`,
                    backgroundSize: '200% 100%',
                    animation: `${shimmer} 1.5s ease-in-out infinite`,
                  },
                }),
              }}
            >
              {isLoading ? (
                <CircularProgress size={20} thickness={3} sx={{ color: colors.accent.main }} />
              ) : (
                <MicrosoftLogo />
              )}
              <span>{isLoading ? 'Redirecting...' : 'Continue with Microsoft'}</span>
            </Box>

            {/* Divider */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                my: 3,
              }}
            >
              <Box sx={{ flex: 1, height: '1px', background: colors.border.subtle }} />
              <Typography
                sx={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.tertiary,
                  fontWeight: typography.fontWeight.medium,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                enterprise SSO
              </Typography>
              <Box sx={{ flex: 1, height: '1px', background: colors.border.subtle }} />
            </Box>

            {/* Security note */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.75,
                color: colors.text.tertiary,
              }}
            >
              <LockIcon />
              <Typography
                sx={{
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.regular,
                  color: colors.text.tertiary,
                  fontFamily: typography.fontFamily.primary,
                }}
              >
                Secured by Azure Active Directory
              </Typography>
            </Box>
          </Box>

          {/* Back to home link */}
          <Box sx={{ mt: 4.5, animation: prefersReducedMotion ? 'none' : `${fadeUp} 0.7s ease-out 0.3s both` }}>
            <Typography
              component="a"
              href="/"
              sx={{
                fontSize: typography.fontSize.sm,
                color: colors.text.tertiary,
                textDecoration: 'none',
                fontWeight: typography.fontWeight.regular,
                fontFamily: typography.fontFamily.primary,
                transition: `color ${spacing.transitions.fast}`,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                '&:hover': { color: colors.text.secondary },
                '&:focus-visible': {
                  outline: `2px solid ${colors.accent.main}`,
                  outlineOffset: '2px',
                  borderRadius: spacing.borderRadius.sm,
                },
              }}
            >
              <ArrowLeftIcon />
              Back to home
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          textAlign: 'center',
          borderTop: `1px solid ${colors.border.subtle}`,
          flexShrink: 0,
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: typography.fontSize.xs,
            color: colors.text.tertiary,
            fontFamily: typography.fontFamily.primary,
          }}
        >
          {`\u00A9 ${new Date().getFullYear()} Tenarai`}
        </Typography>
      </Box>
    </Box>
  )
}

export default Login
